/**
 * SACharts — 3 Chart.js visualizations for the Superadmin cockpit
 * Pattern: CockpitCharts.js (ResizeObserver, same tokens)
 */
import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import GlassCard from '../shared/GlassCard';
import { colors, chartColors } from '../../styles/colors';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement,
  LineElement, ArcElement, Title, Tooltip, Legend, Filler
);

const FONT_FAMILY = '"DM Sans", sans-serif';

const commonTooltip = {
  backgroundColor: colors.charcoal,
  cornerRadius: 8,
  titleFont: { family: FONT_FAMILY, size: 12 },
  bodyFont: { family: FONT_FAMILY, size: 11 },
  padding: 10,
};

const gridColor = colors.borderLight;

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function SectionTitle({ children }) {
  return (
    <Typography
      sx={{
        fontFamily: '"Playfair Display", Georgia, serif',
        fontSize: '1.2rem',
        fontWeight: 600,
        color: colors.charcoal,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

function ChartContainer({ children, height = 200 }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      const canvas = el.querySelector('canvas');
      if (canvas) {
        const chartInstance = ChartJS.getChart(canvas);
        if (chartInstance) chartInstance.resize();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', height, minWidth: 0 }}>
      {children}
    </Box>
  );
}

// --- Doughnut: Revenue by agency ---
const centerTextPlugin = {
  id: 'saCenterText',
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!chart.config.options.plugins.saCenterText?.display) return;
    const { text, subText } = chart.config.options.plugins.saCenterText;
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `800 1.75rem "DM Sans", sans-serif`;
    ctx.fillStyle = colors.charcoal;
    ctx.fillText(text, centerX, centerY - 8);
    ctx.font = `400 0.7rem "DM Sans", sans-serif`;
    ctx.fillStyle = colors.gray400;
    ctx.fillText(subText, centerX, centerY + 14);
    ctx.restore();
  },
};

function RevenueDoughnut({ data, agencies }) {
  if (!data || Object.keys(data).length === 0) return null;

  const entries = Object.entries(data);
  const total = entries.reduce((sum, [, v]) => sum + v, 0);
  const seriesColors = chartColors.series;

  // Use agency accent_color if available, fallback to series colors
  const agencyColorMap = {};
  if (agencies) {
    agencies.forEach(a => { if (a.accent_color) agencyColorMap[a.name] = a.accent_color; });
  }

  const chartData = {
    labels: entries.map(([name]) => name),
    datasets: [{
      data: entries.map(([, val]) => val),
      backgroundColor: entries.map(([name], i) => agencyColorMap[name] || seriesColors[i % seriesColors.length]),
      borderWidth: 0,
      hoverOffset: 4,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    cutout: '62%',
    animation: { animateRotate: true, duration: 800, easing: 'easeOutQuart' },
    plugins: {
      legend: { display: false },
      tooltip: commonTooltip,
      saCenterText: {
        display: true,
        text: total.toLocaleString('fr-FR'),
        subText: 'EUR/mois',
      },
    },
  };

  return (
    <Box>
      <SectionTitle>Repartition revenus</SectionTitle>
      <ChartContainer height={220}>
        <Doughnut data={chartData} options={options} plugins={[centerTextPlugin]} />
      </ChartContainer>
      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
        {entries.map(([name], i) => (
          <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: agencyColorMap[name] || seriesColors[i % seriesColors.length], flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.7rem', color: colors.gray500, fontFamily: FONT_FAMILY }}>
              {name}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// --- Bar: Cost vs Revenue ---
function CostVsRevenueBar({ data }) {
  if (!data) return null;

  const chartData = {
    labels: data.labels || [],
    datasets: [
      {
        label: 'Revenu',
        data: data.revenue || [],
        backgroundColor: hexToRgba(colors.success, 0.6),
        borderRadius: 6,
        maxBarThickness: 48,
      },
      {
        label: 'Cout IA',
        data: data.ai_cost || [],
        backgroundColor: hexToRgba(colors.danger, 0.6),
        borderRadius: 6,
        maxBarThickness: 48,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    plugins: {
      legend: { display: true, labels: { font: { family: FONT_FAMILY, size: 11 }, color: colors.textMed } },
      tooltip: commonTooltip,
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { family: FONT_FAMILY, size: 11 }, color: colors.textMed },
      },
      y: {
        grid: { color: gridColor },
        ticks: { font: { family: FONT_FAMILY, size: 11 }, color: colors.textLight },
      },
    },
  };

  return (
    <Box>
      <SectionTitle>Cout IA vs Revenu</SectionTitle>
      <ChartContainer height={200}>
        <Bar data={chartData} options={options} />
      </ChartContainer>
    </Box>
  );
}

// --- Line: MRR Evolution ---
function MRREvolutionLine({ data }) {
  if (!data) return null;

  const datasets = [
    {
      label: 'MRR Total',
      data: data.total || [],
      borderColor: colors.primary,
      backgroundColor: hexToRgba(colors.primary, 0.1),
      fill: true,
      tension: 0.4,
      pointRadius: 2,
      borderWidth: 2,
    },
  ];

  if (data.best?.values) {
    datasets.push({
      label: data.best.name || 'Meilleure',
      data: data.best.values,
      borderColor: hexToRgba(colors.primary, 0.4),
      borderDash: [6, 4],
      fill: false,
      tension: 0.4,
      pointRadius: 0,
      borderWidth: 1.5,
    });
  }

  if (data.worst?.values) {
    datasets.push({
      label: data.worst.name || 'Plus faible',
      data: data.worst.values,
      borderColor: hexToRgba(colors.danger, 0.4),
      borderDash: [6, 4],
      fill: false,
      tension: 0.4,
      pointRadius: 0,
      borderWidth: 1.5,
    });
  }

  const chartData = { labels: data.labels || [], datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    plugins: {
      legend: { display: false },
      tooltip: commonTooltip,
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { family: FONT_FAMILY, size: 11 }, color: colors.textMed },
      },
      y: {
        grid: { color: gridColor },
        ticks: { font: { family: FONT_FAMILY, size: 11 }, color: colors.textLight },
      },
    },
  };

  return (
    <Box>
      <SectionTitle>Evolution MRR</SectionTitle>
      <ChartContainer height={200}>
        <Line data={chartData} options={options} />
      </ChartContainer>
    </Box>
  );
}

// --- Main ---
function SACharts({ charts, agencies }) {
  if (!charts) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        <GlassCard sx={{ p: 4, minWidth: 0, overflow: 'hidden' }}>
          <RevenueDoughnut data={charts.revenue_by_agency} agencies={agencies} />
        </GlassCard>
        <GlassCard sx={{ p: 4, minWidth: 0, overflow: 'hidden' }}>
          <CostVsRevenueBar data={charts.cost_vs_revenue} />
        </GlassCard>
      </Box>
      <GlassCard sx={{ p: 4, minWidth: 0, overflow: 'hidden' }}>
        <MRREvolutionLine data={charts.mrr_evolution} />
      </GlassCard>
    </Box>
  );
}

export default SACharts;
